import { api } from '../lib/api';
import { normalizeTotals } from '../lib/reactions';
import type {
  Comment,
  CommentsResponse,
  CreateReactionBody,
  Reaction,
  ReactionsResponse,
  SharePayload,
} from '../lib/types';

/**
 * The share surface is deliberately auth-optional: the share id *is* the
 * credential. A signed-in viewer sends their token so private team videos
 * resolve, and so their view is attributed to their account instead of an
 * anonymous browser id.
 */

export function fetchShare(shareId: string): Promise<SharePayload> {
  return api<SharePayload>(`/v1/share/${shareId}`, { auth: 'optional' });
}

export function fetchComments(shareId: string): Promise<Comment[]> {
  return api<CommentsResponse>(`/v1/share/${shareId}/comments`, { auth: 'optional' }).then(
    (response) => response.comments,
  );
}

export function postComment(
  shareId: string,
  body: { bodyText: string; authorName?: string; timecodeSec?: number },
): Promise<Comment> {
  return api<Comment>(`/v1/share/${shareId}/comments`, { method: 'POST', body, auth: 'optional' });
}

/**
 * Reactions use the same auth-optional rule as comments: the share id is the
 * credential, and a 404 means "not yours to see" as much as it means "gone".
 */
export function fetchReactions(shareId: string): Promise<ReactionsResponse> {
  return api<ReactionsResponse>(`/v1/share/${shareId}/reactions`, { auth: 'optional' }).then(
    (response) => ({
      reactions: Array.isArray(response?.reactions) ? response.reactions : [],
      totals: normalizeTotals(response?.totals),
    }),
  );
}

export function postReaction(shareId: string, body: CreateReactionBody): Promise<Reaction> {
  return api<Reaction>(`/v1/share/${shareId}/reactions`, {
    method: 'POST',
    body,
    auth: 'optional',
  });
}

export function postView(
  shareId: string,
  body: { anonId?: string; progressSec?: number; completed?: boolean },
): Promise<void> {
  return api<void>(`/v1/share/${shareId}/views`, { method: 'POST', body, auth: 'optional' });
}
